import React from 'react';
import { Box, Button, IconButton, MenuItem, Stack, TextField } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';
import { FilterValue, TableFilters as Filters, hasActiveFilters } from './filters';

/** Une option de liste déroulante, forme que produisent tous les hooks de référentiel. */
export type FilterOption = { value: string | number; label: string };

/**
 * Un filtre de la barre.
 *
 * `name` est le paramètre envoyé à l'API. `multiple` distingue une sélection multiple
 * (valeur `(string | number)[]`) d'un choix unique.
 */
export type FilterField = {
  name: string;
  labelKey: string;
  options: FilterOption[];
  multiple?: boolean;
  /**
   * Mot affiché quand rien n'est choisi : « Tous » ou « Toutes » selon le genre du
   * libellé. i18next n'accorde pas, c'est donc au champ de le dire.
   */
  allLabelKey?: string;
};

type FilterChange = (name: string, value: FilterValue) => void;

/** Largeur commune : les listes s'alignent quelle que soit la longueur des libellés. */
const FIELD_WIDTH = 200;

/** Une valeur d'option, telle que la rend le Select : chaîne ou identifiant numérique. */
function isOptionValue(value: unknown): value is string | number {
  return typeof value === 'string' || typeof value === 'number';
}

/**
 * Valeur retenue par un changement de sélection.
 *
 * MUI type en `unknown` la valeur d'un `SelectChangeEvent` : à l'exécution, c'est la
 * `value` de l'option choisie, ou le tableau des `value` cochées quand le champ est
 * multiple. Les deux formes se lisent donc par garde de type.
 *
 * Un choix unique est ramené à une chaîne — forme que retient `FilterValue` et qu'attend
 * le paramètre d'URL. À défaut, la chaîne vide, qui est l'option « Tous ».
 */
function selectedValue(value: unknown): FilterValue {
  if (Array.isArray(value)) {
    return value.filter(isOptionValue);
  }

  return isOptionValue(value) ? String(value) : '';
}

function currentValue(values: Filters, field: FilterField): FilterValue {
  const value = values[field.name];

  if (field.multiple) {
    return Array.isArray(value) ? value : [];
  }

  return Array.isArray(value) || value === undefined ? null : value;
}

/**
 * Un filtre isolé. Exporté parce que l'étape Demandes présente « État » dans sa barre
 * d'outils, hors du bloc dépliable — c'est ce que montre la maquette.
 */
export function FilterSelect({
  field,
  values,
  onChange
}: {
  field: FilterField;
  values: Filters;
  onChange: FilterChange;
}) {
  const { t } = useTranslation();
  const value = currentValue(values, field);

  /** Valeurs réellement posées : la visibilité de la croix et le libellé s'y adossent. */
  const filled = (Array.isArray(value) ? value : [value]).filter(
    (item) => item !== null && item !== ''
  );

  /**
   * « État : Tous », « Commune : Changé », « Commune (3) » — le libellé porte toujours
   * le choix courant, comme sur la maquette.
   */
  const summary = (): string => {
    const name = t(field.labelKey);

    if (filled.length === 0) {
      return `${name} : ${t(field.allLabelKey ?? 'interventionCreation.filters.all')}`;
    }

    if (filled.length > 1) {
      return `${name} (${filled.length})`;
    }

    const option = field.options.find((item) => String(item.value) === String(filled[0]));

    return `${name} : ${option?.label ?? String(filled[0])}`;
  };

  /** La chaîne vide, et non `null` : c'est la valeur de l'option « Tous ». */
  const clear = () => onChange(field.name, field.multiple ? [] : '');

  return (
    <TextField
      select
      size="small"
      sx={{ width: FIELD_WIDTH }}
      InputProps={{
        // Le fond se pose sur le champ et non sur le TextField : l'arrondi est porté par
        // `.MuiOutlinedInput-root`, un fond posé sur le conteneur déborderait des angles.
        sx: { bgcolor: 'background.paper' },
        endAdornment:
          filled.length > 0 ? (
            <IconButton
              size="small"
              aria-label={t('interventionCreation.filters.clear')}
              onClick={clear}
              // Le chevron du Select est en absolu à `right: 7px` et large de 24px : il
              // occupe la bande 7→31px. Le conteneur ayant 14px de retrait droit quand il
              // porte un `endAdornment`, 20px de marge posent le bord droit de la croix à
              // 34px — juste au-delà du chevron, sans avoir à déplacer celui-ci.
              sx={{ mr: 2.5 }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          ) : undefined
      }}
      SelectProps={{
        multiple: field.multiple,
        // Sans `displayEmpty`, MUI n'affiche rien pour une valeur vide, or c'est
        // justement l'état « Tous » qui doit se lire.
        displayEmpty: true,
        renderValue: summary,
        // Le `onChange` se pose ici, sur le Select, et non sur le TextField : celui du
        // TextField est typé sur un `HTMLInputElement` et ne rend donc qu'une `string`,
        // alors qu'une sélection multiple reçoit un tableau à l'exécution.
        onChange: (event: SelectChangeEvent<unknown>) =>
          onChange(field.name, selectedValue(event.target.value))
      }}
      value={value ?? ''}
      inputProps={{ 'aria-label': t(field.labelKey) }}
    >
      {field.options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

/**
 * Barre de filtres commune aux deux étapes, dépliée par le bouton « Filtres ».
 *
 * Chaque étape fournit sa propre liste de champs : les référentiels et les paramètres
 * acceptés par l'API ne sont pas les mêmes de part et d'autre.
 */
export default function TableFilters({
  fields,
  values,
  onChange,
  onReset,
  children,
  extraNames = [],
  inlineReset = false
}: {
  fields: FilterField[];
  values: Filters;
  onChange: FilterChange;
  onReset: () => void;
  /** Filtres d'une autre forme que les listes (périodes…), posés à leur suite sur la même ligne. */
  children?: React.ReactNode;
  /** Paramètres que portent ces filtres : ils comptent pour activer « Réinitialiser ». */
  extraNames?: string[];
  /**
   * « Réinitialiser » sur la ligne des filtres, poussé à droite, au lieu de sa ligne propre en
   * dessous. Réservé aux écrans au nouveau design (suivi des interventions) : les autres
   * appelants gardent la disposition d'origine.
   */
  inlineReset?: boolean;
}) {
  const { t } = useTranslation();

  // Désactivé tant que rien n'est filtré : c'est aussi ce qui signale que la liste affichée
  // est restreinte. Restreint aux champs du bloc, seuls que le bouton videra — l'état et la
  // recherche vivent hors d'ici.
  const resetButton = (
    <Button
      size="small"
      sx={inlineReset ? { ml: 'auto' } : undefined}
      disabled={!hasActiveFilters(values, [...fields.map((field) => field.name), ...extraNames])}
      onClick={onReset}
    >
      {t('interventionCreation.filters.reset')}
    </Button>
  );

  return (
    <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          spacing={1.5}
          flexWrap="wrap"
          useFlexGap
          alignItems={inlineReset ? 'center' : undefined}
        >
          {fields.map((field) => (
            <FilterSelect key={field.name} field={field} values={values} onChange={onChange} />
          ))}
          {children}
          {inlineReset && resetButton}
        </Stack>

        {!inlineReset && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>{resetButton}</Box>
        )}
      </Stack>
    </Box>
  );
}
